// PageContainer (009 revision 6, FR-046, FR-048): the page's content box: the page gutter of the breakpoint on both
// sides (16 px below 768 px, 24 px from 768 px, 32 px from 1280 px), the same vertical rhythm on every page, and a
// width kind: `content` (the staff pages, at most 1440 px), `reading` (the reader's column, at most 640 px, so
// centred from 768 px) or `full`. It declares its kind (`data-ui-container`) for the UI check's container rule.
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Props of {@link PageContainer}: its own and any attribute of the element (an accessible name, a data mark). */
export interface PageContainerProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** `content` (default), `reading` or `full`. */
  width?: 'content' | 'reading' | 'full';
  /** The element: `main` for the page's main landmark (default), `div` or `section` inside one. */
  as?: 'main' | 'div' | 'section';
  /** An id (the skip link's target). */
  id?: string;
  className?: string;
  children: ReactNode;
}

const widths = { content: 'max-w-content', reading: 'max-w-reading', full: '' };

/**
 * The page container.
 * @param props width kind, element, id, classes and content
 * @returns the container
 */
export function PageContainer({
  width = 'content',
  as: Element = 'main',
  id,
  className,
  children,
  ...rest
}: PageContainerProps) {
  return (
    <Element
      {...rest}
      id={id}
      data-ui-container={width === 'full' ? undefined : width}
      data-ui-part="page"
      className={cn(
        'mx-auto flex w-full min-w-0 flex-col gap-section px-gutter pt-page-top pb-gutter',
        widths[width],
        className,
      )}
    >
      {children}
    </Element>
  );
}
